import { useEffect, useRef, useState } from "react";
import { useDispatchApp } from "../../../lib/redux";
import { startGetGlobalReportSuggestedKpi } from "../../../actions/adsCampaigns";
import { startGetAdvertiserKpiSummary } from "../../../actions/user";
import { useReportSnapshot } from "../../../features/reportPdf/ReportSnapshotContext";
import { hasAdvertiserPrimaryKpiConfigured } from "../../AdminDashboard/utils/kpiSummaryMerge";

import { LaunchError } from "../../../utils/errors";

const syncMeasurableKpiFromSummary = ({
  summary,
  hasUserSetMeasurableKpi,
  skipMeasurableKpiChangeRef,
  setValue,
}) => {
  if (hasUserSetMeasurableKpi || !summary?.primaryKpi) {
    return;
  }

  if (skipMeasurableKpiChangeRef) {
    skipMeasurableKpiChangeRef.current = true;
  }

  setValue("measurableKpi", summary.primaryKpi.trim(), {
    shouldDirty: true,
    shouldTouch: true,
  });
};

const useGlobalReportSuggestedKpi = ({
  advertiserID,
  kpiSummary,
  isLoadingKpiSummary,
  hasUserSetMeasurableKpi,
  setValue,
  skipMeasurableKpiChangeRef,
  onKpiSummarySaved,
  skipAutoSuggest = false,
}) => {
  const dispatch = useDispatchApp();
  const snapshot = useReportSnapshot();
  const [isSuggestingKpi, setIsSuggestingKpi] = useState(false);
  const [suggestedKpi, setSuggestedKpi] = useState(null);
  const [suggestionError, setSuggestionError] = useState(null);
  const suggestedForAdvertiserRef = useRef(null);
  const kpiSummaryRef = useRef(kpiSummary);
  const hasUserSetMeasurableKpiRef = useRef(hasUserSetMeasurableKpi);

  useEffect(() => {
    kpiSummaryRef.current = kpiSummary;
  }, [kpiSummary]);

  useEffect(() => {
    hasUserSetMeasurableKpiRef.current = hasUserSetMeasurableKpi;
  }, [hasUserSetMeasurableKpi]);

  useEffect(() => {
    if (
      snapshot ||
      skipAutoSuggest ||
      !advertiserID ||
      isLoadingKpiSummary ||
      hasUserSetMeasurableKpi
    ) {
      return undefined;
    }

    if (hasAdvertiserPrimaryKpiConfigured(kpiSummary)) {
      return undefined;
    }

    if (suggestedForAdvertiserRef.current === advertiserID) {
      return undefined;
    }

    let isActive = true;

    const applySuggestedKpi = (nextPrimaryKpi, summaryBase = null) => {
      const mergedSummary = {
        ...(summaryBase || kpiSummaryRef.current || {}),
        primaryKpi: nextPrimaryKpi,
      };

      syncMeasurableKpiFromSummary({
        summary: mergedSummary,
        hasUserSetMeasurableKpi: hasUserSetMeasurableKpiRef.current,
        skipMeasurableKpiChangeRef,
        setValue,
      });
      onKpiSummarySaved({ advertiserID, kpiSummary: mergedSummary });
      setSuggestedKpi(nextPrimaryKpi);
      suggestedForAdvertiserRef.current = advertiserID;
    };

    const suggestKpi = async () => {
      setSuggestionError(null);

      try {
        const freshResponse = await dispatch(
          startGetAdvertiserKpiSummary({ advertiserID })
        );

        if (!isActive) {
          return;
        }

        const freshSummary = freshResponse?.ok
          ? (freshResponse.kpiSummary ?? null)
          : null;

        if (hasAdvertiserPrimaryKpiConfigured(freshSummary)) {
          onKpiSummarySaved({ advertiserID, kpiSummary: freshSummary });
          syncMeasurableKpiFromSummary({
            summary: freshSummary,
            hasUserSetMeasurableKpi: hasUserSetMeasurableKpiRef.current,
            skipMeasurableKpiChangeRef,
            setValue,
          });
          suggestedForAdvertiserRef.current = advertiserID;
          return;
        }

        setIsSuggestingKpi(true);

        const response = await dispatch(startGetGlobalReportSuggestedKpi());

        if (!isActive) {
          return;
        }

        if (!response?.ok || !response.data?.primaryKpi) {
          setSuggestionError(new Error("suggestion_failed"));
          return;
        }

        applySuggestedKpi(response.data.primaryKpi.trim(), freshSummary);
      } catch (error) {
        dispatch(
          LaunchError(error, false, [], {
            functionName: "suggestKpi",
            capture: "exception",
          })
        );
        if (isActive) {
          setSuggestionError(error);
        }
      } finally {
        if (isActive) {
          setIsSuggestingKpi(false);
        }
      }
    };

    suggestKpi();

    return () => {
      isActive = false;
    };
  }, [
    advertiserID,
    dispatch,
    hasUserSetMeasurableKpi,
    isLoadingKpiSummary,
    kpiSummary,
    onKpiSummarySaved,
    setValue,
    skipMeasurableKpiChangeRef,
    skipAutoSuggest,
    snapshot,
  ]);

  return {
    isSuggestingKpi,
    suggestedKpi,
    suggestionError,
  };
};

export default useGlobalReportSuggestedKpi;
